"use client";

import { useState, useEffect, useMemo } from "react";
import { IconReload, IconEdit, IconHelpTriangle } from "@tabler/icons-react";
import { Button, Tabs, Tab, Accordion, AccordionItem } from "@heroui/react";
import { hyper } from "@/components/Decklists/TriggerBodies";
import { AgGridReact } from "ag-grid-react"; // React Data Grid Component
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  Theme,
} from "ag-grid-community";
import {
  ModuleRegistry,
  themeQuartz,
  colorSchemeDark,
} from "ag-grid-community";

const myTheme = themeQuartz.withPart(colorSchemeDark);

// Remove old CSS imports
// import "ag-grid-community/styles/ag-grid.css";
// import "ag-grid-community/styles/ag-theme-quartz.css";
// import "ag-grid-community/styles/ag-theme-alpine.css";

// Register AG Grid Modules
ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule]);

const CustomHeader = (props: any) => {
  return (
    <div className="custom-header">
      <IconEdit height={18} width={18} />
      <span>{props.displayName}</span>
    </div>
  );
};

function getGoingFirstData(
  costDict: Record<string, number>,
  goingFirst: string,
) {
  let data = [];

  if (goingFirst === "first") {
    for (let i = 0; i < 6; i++) {
      const curDon = Math.min(i * 2 + 1, 10);
      let onCurveKey = curDon.toString();
      if (curDon === 10) {
        onCurveKey = "10+";
      }
      data.push({
        turn: i + 1,
        don: curDon,
        onCurvePlays: costDict[onCurveKey],
        cardsDrawn: 5 + i,
      });
    }
  } else {
    for (let i = 0; i < 5; i++) {
      const curDon = Math.min(i * 2 + 2, 10);
      let onCurveKey = curDon.toString();
      if (curDon === 10) {
        onCurveKey = "10+";
      }
      data.push({
        turn: i + 1,
        don: curDon,
        onCurvePlays: costDict[onCurveKey],
        cardsDrawn: 5 + i + 1,
      });
    }
  }
  return data;
}

export function OnCurvePlays({ costDict }: { costDict: any }) {
  const [goingFirst, setGoingFirst] = useState("first");
  const [rowData, setRowData] = useState(
    getGoingFirstData(costDict, goingFirst),
  );

  function reset() {
    let x = getGoingFirstData(costDict, goingFirst);
    setRowData(x);
  }

  useEffect(() => {
    let x = getGoingFirstData(costDict, goingFirst);
    setRowData(x);
    // onCellValueChanged("");
  }, [goingFirst, costDict]);

  return (
    <div>
      <div className="text-lg font-semibold">On Curve Plays</div>
      <Accordion isCompact variant="light" className="my-2 px-0">
        <AccordionItem
          key="1"
          aria-label="Leader Distribution"
          title={
            <div className="flex flex-row gap-2 items-center">
              <IconHelpTriangle />
              How to Use?
            </div>
          }
        >
          <p className="my-2">
            This module serves to answer questions like{" "}
            <em>
              &apos;What is the probability I can play Moria on curve?&apos;
            </em>
          </p>
          <p className="my-2">
            By default this module assumes you draw 1 card per turn. However if
            you want to factor in cycling (like Sakazuki leader effect), drawing
            cards from life, or other effects double click and edit the{" "}
            <b>Cards Drawn</b> column and increase to the number of cards you
            expect to have seen by that time. If you are curious what happens
            when you add additional hits, e.g. another 8 drop, double click and
            edit the <b>On Curve Plays</b> column.
          </p>
          <p className="my-2">
            So what is the chance of playing Moria on curve? Playing 4 copies of
            Moria, going first on 9 don turn or going second on 8 don turn there
            is a 56% chance of seeing Moria within 9 cards! You should expect to
            see Moria on curve in more than half of your games!
          </p>
        </AccordionItem>
      </Accordion>
      <div className="flex items-center gap-2 my-2">
        <Tabs
          aria-label="Options"
          selectedKey={goingFirst}
          onSelectionChange={setGoingFirst as any}
        >
          <Tab key="first" title="Going First" />
          <Tab key="second" title="Going Second" />
        </Tabs>
        <Button
          isIconOnly
          radius="full"
          className="min-w-0 min-h-0 h-[30px] w-[30px]"
          onPress={() => {
            reset();
          }}
        >
          <IconReload height={18} width={18} />
        </Button>
      </div>
      <OnCurvePlaysGrid
        goingFirst={goingFirst}
        costDict={costDict}
        rowData={rowData}
        setRowData={setRowData}
      />
    </div>
  );
}

function OnCurvePlaysGrid({
  goingFirst,
  costDict,
  rowData,
  setRowData,
}: {
  goingFirst: string;
  costDict: any;
  rowData: any;
  setRowData: any;
}) {
  //   const [rowData, setRowData] = useState(
  //     getGoingFirstData(costDict, goingFirst),
  //   );

  const autoSizeStrategy = {
    type: "fitGridWidth",
    defaultMinWidth: 100,
    // columnLimits: [
    //   {
    //     colId: "country",
    //     minWidth: 900,
    //   },
    // ],
  };

  // Column Definitions: Defines the columns to be displayed.
  const [colDefs, setColDefs] = useState([
    { field: "turn", headerName: "Turn" },
    { field: "don", headerName: "Don !!" },
    {
      field: "onCurvePlays",
      headerName: "On Curve Plays (Edit)",
      minWidth: 200,
      //   headerComponentFramework: CustomHeader,
      editable: true,
    },
    {
      field: "cardsDrawn",
      headerName: "Cards Drawn (Edit)",
      minWidth: 200,
      //   headerComponentFramework: CustomHeader,
      editable: true,
    },
    {
      field: "probability",
      headerName: "Probability of At Least 1",
      minWidth: 200,
      valueGetter: (params: any) =>
        Math.round(
          1000 -
            hyper(0, params.data.onCurvePlays, params.data.cardsDrawn) * 1000,
        ) /
          10 +
        "%",
    },
  ]);

  function formatNumber(number: number) {
    return Math.floor(number).toLocaleString();
  }

  const defaultColDef = useMemo<any>(() => {
    return {
      minWidth: 100,
      flex: 1,
      //   cellClass: "align-right",
      //   enableCellChangeFlash: true,
      flashDuration: 10000,
      //   valueFormatter: (params: any) => {
      //     return formatNumber(params.value);
      //   },
    };
  }, []);

  const onCellValueChanged = (event: any) => {
    setRowData([...rowData]);
  };
  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        { statusPanel: "agTotalAndFilteredRowCountComponent" },
        { statusPanel: "agTotalRowCountComponent" },
        { statusPanel: "agFilteredRowCountComponent" },
        { statusPanel: "agSelectedRowCountComponent" },
        { statusPanel: "agAggregationComponent" },
      ],
    };
  }, []);

  return (
    <div>
      <div>
        <AgGridReact
          // statusBar={statusBar}
          domLayout="autoHeight"
          rowData={rowData}
          columnDefs={colDefs}
          defaultColDef={defaultColDef}
          onCellValueChanged={onCellValueChanged}
          autoSizeStrategy={autoSizeStrategy as any}
          rowModelType="clientSide"
          theme={myTheme}
        />
      </div>
    </div>
  );
}
